import React, { useState, useEffect, useCallback } from "react";
import {
  Activity, Zap, AlertTriangle, CheckCircle2, RefreshCw, Database,
  Cpu, Clock, TrendingUp, Image as ImageIcon, Upload,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HealthBadge = ({ health }) => {
  const palette = {
    green: "bg-emerald-100 text-emerald-800 border-emerald-300",
    yellow: "bg-amber-100 text-amber-800 border-amber-300",
    red: "bg-rose-100 text-rose-800 border-rose-300",
  }[health] || "bg-gray-100 text-gray-700 border-gray-200";
  const label = { green: "Healthy", yellow: "Degraded", red: "Incident" }[health] || health;
  const Icon = health === "green" ? CheckCircle2 : AlertTriangle;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-semibold ${palette}`} data-testid={`magic-scale-health-${health}`}>
      <Icon size={12} /> {label}
    </span>
  );
};

const Tile = ({ label, value, icon: Icon, color = "violet", hint }) => (
  <div className={`bg-white border border-${color}-100 rounded-xl p-4 shadow-sm`}>
    <div className="flex items-center gap-1.5 text-xs text-[#64748B] mb-1">
      <Icon size={13} className={`text-${color}-500`} /> {label}
    </div>
    <div className={`text-2xl font-bold text-[#1E1B4B] tabular-nums`}>{value}</div>
    {hint && <div className="text-[11px] text-[#94A3B8] mt-1">{hint}</div>}
  </div>
);

const TierBar = ({ label, count, total, color }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-semibold text-[#1E1B4B]">{label}</span>
        <span className="text-[#64748B] tabular-nums">{count} · {pct}%</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full bg-${color}-500 transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default function MagicScaleTab({ authAxios }) {
  const [metrics, setMetrics] = useState(null);
  const [lite, setLite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [hours, setHours] = useState(24);

  const loadFull = useCallback(async () => {
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/magic/scale-metrics?hours=${hours}`);
      setMetrics(r.data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load metrics");
    }
    setLoading(false);
  }, [authAxios, hours]);

  const loadLite = useCallback(async () => {
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/magic/scale-metrics/lite`);
      setLite(r.data);
    } catch { /* ignore — best effort */ }
  }, [authAxios]);

  // Full metrics every 15s (has DB aggregations)
  useEffect(() => {
    loadFull();
    const iv = setInterval(loadFull, 15000);
    return () => clearInterval(iv);
  }, [loadFull]);

  // Lite metrics every 3s for real-time feel (no DB)
  useEffect(() => {
    loadLite();
    const iv = setInterval(loadLite, 3000);
    return () => clearInterval(iv);
  }, [loadLite]);

  const runImport = async (dryRun = false) => {
    setImporting(true);
    setImportResult(null);
    try {
      const r = await authAxios.post(`${API_URL}/api/admin/reference-gallery/import-from-ai-gallery?dry_run=${dryRun}`);
      setImportResult(r.data);
      if (!dryRun) await loadFull();
    } catch (e) {
      setImportResult({ error: e?.response?.data?.detail || "Import failed" });
    }
    setImporting(false);
  };

  // Use `lite` for live numbers so users see 3s updates; `metrics` for distribution & latency
  const inflight = lite?.inflight ?? metrics?.inflight?.count ?? 0;
  const inflightMax = lite?.backpressure_threshold ?? metrics?.inflight?.backpressure_threshold ?? 80;
  const backpressureActive = lite?.under_backpressure ?? metrics?.inflight?.under_backpressure ?? false;
  const circuitOpen = lite?.circuit_open ?? metrics?.circuit?.is_open ?? false;
  const cacheCount = lite?.cache_count ?? metrics?.cache?.count ?? 0;
  const geminiSlow = lite?.gemini_slow ?? false;
  const geminiP50 = lite?.gemini_p50_60s_ms ?? 0;

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-[#64748B] text-sm gap-2">
      <RefreshCw size={14} className="animate-spin" /> Loading Magic Scale metrics…
    </div>;
  }

  const tiers = metrics?.tiers || { gemini: 0, openai: 0, cached: 0, failed: 0 };
  const totalTier = tiers.gemini + tiers.openai + tiers.cached;
  const lat = metrics?.latency || {};
  const coverage = metrics?.cache?.coverage || { well_covered: 0, thin: 0, empty: 0 };
  const thinCombos = metrics?.cache?.thin_combos || [];
  const jobs = metrics?.jobs_summary || { total: 0, completed: 0, failed: 0, success_rate_pct: 100 };

  return (
    <div className="space-y-5" data-testid="magic-scale-tab">
      {/* Header + health + refresh */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#1E1B4B] flex items-center gap-2">
            <Activity className="text-[#DC283C]" /> Magic Scale
          </h2>
          <p className="text-xs text-[#64748B] mt-1">
            Live pipeline health · updates every 3s · 24h distribution refresh every 15s
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {metrics && <HealthBadge health={metrics.health} />}
          <select
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="text-xs px-3 py-1.5 border border-gray-200 rounded-xl bg-white"
            data-testid="magic-scale-hours-select"
          >
            <option value="1">Last 1 hour</option>
            <option value="6">Last 6 hours</option>
            <option value="24">Last 24 hours</option>
            <option value="72">Last 72 hours</option>
          </select>
          <button onClick={loadFull} className="text-xs px-3 py-1.5 border border-gray-200 rounded-xl hover:bg-gray-50 flex items-center gap-1" data-testid="magic-scale-refresh">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-xl px-3 py-2 text-xs" data-testid="magic-scale-error">
          {error}
        </div>
      )}

      {/* LIVE TILES — animate inflight number */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile
          label="In-flight AI jobs"
          value={inflight}
          icon={Cpu}
          color={backpressureActive ? "rose" : inflight > inflightMax * 0.6 ? "amber" : "emerald"}
          hint={`Backpressure at ${inflightMax} · cap ${lite?.semaphore_capacity || 200}`}
        />
        <Tile
          label="Circuit breaker"
          value={circuitOpen ? "OPEN" : "CLOSED"}
          icon={circuitOpen ? AlertTriangle : CheckCircle2}
          color={circuitOpen ? "rose" : "emerald"}
          hint={circuitOpen
            ? `Reopens in ${metrics?.circuit?.seconds_until_close || 0}s`
            : `${lite?.circuit_fails_60s || 0} fails in last 60s`}
        />
        <Tile
          label="Cache size"
          value={cacheCount}
          icon={Database}
          color="violet"
          hint={`${coverage.well_covered} combos well-covered · ${coverage.thin} thin · ${coverage.empty} empty`}
        />
        <Tile
          label="Success rate (24h)"
          value={`${jobs.success_rate_pct}%`}
          icon={TrendingUp}
          color={jobs.success_rate_pct >= 95 ? "emerald" : jobs.success_rate_pct >= 85 ? "amber" : "rose"}
          hint={`${jobs.completed}/${jobs.total} completed · ${jobs.failed} failed`}
        />
      </div>

      {/* BACKPRESSURE BAR — visual meter of inflight vs threshold */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h3 className="text-sm font-bold text-[#1E1B4B]">Live concurrency</h3>
          <div className="flex items-center gap-2 flex-wrap">
            {geminiSlow && (
              <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full" data-testid="magic-scale-gemini-slow">
                ⏱ Gemini slow (p50 {(geminiP50 / 1000).toFixed(1)}s) — auto-diverting to cache
              </span>
            )}
            <span className={`text-xs font-semibold ${backpressureActive ? "text-rose-600" : "text-emerald-600"}`}>
              {backpressureActive ? "⚠️ Backpressure active — new customers see cached preview" : "✓ Free capacity"}
            </span>
          </div>
        </div>
        <div className="h-4 bg-gray-100 rounded-full overflow-hidden relative" data-testid="magic-scale-inflight-bar">
          <div
            className={`h-full transition-all duration-500 ${
              backpressureActive ? "bg-gradient-to-r from-rose-500 to-red-600"
              : inflight > inflightMax * 0.6 ? "bg-gradient-to-r from-amber-400 to-amber-600"
              : "bg-gradient-to-r from-emerald-400 to-emerald-600"
            }`}
            style={{ width: `${Math.min(100, (inflight / inflightMax) * 100)}%` }}
          />
          {/* Threshold marker */}
          <div className="absolute top-0 bottom-0 w-0.5 bg-rose-900 opacity-70" style={{ left: "100%", marginLeft: "-2px" }} />
        </div>
        <div className="flex items-center justify-between text-[11px] text-[#64748B] mt-2 tabular-nums">
          <span>0</span>
          <span className="font-semibold">{inflight} / {inflightMax}</span>
          <span>{inflightMax} (backpressure)</span>
        </div>
      </div>

      {/* 24H TIER DISTRIBUTION */}
      {metrics && (
        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-white border border-gray-200 rounded-2xl p-5">
            <h3 className="text-sm font-bold text-[#1E1B4B] mb-3 flex items-center gap-2">
              <Zap size={14} className="text-amber-500" /> Tier distribution (last {metrics.hours}h)
            </h3>
            {totalTier === 0 ? (
              <div className="text-xs text-[#94A3B8] py-4 text-center">No completed jobs in this window.</div>
            ) : (
              <div className="space-y-3">
                <TierBar label="✨ Gemini (your room)" count={tiers.gemini} total={totalTier} color="violet" />
                <TierBar label="🤖 OpenAI fallback" count={tiers.openai} total={totalTier} color="blue" />
                <TierBar label="📦 Cached preview" count={tiers.cached} total={totalTier} color="emerald" />
                {tiers.failed > 0 && <TierBar label="❌ Hard fail" count={tiers.failed} total={totalTier + tiers.failed} color="rose" />}
              </div>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-5">
            <h3 className="text-sm font-bold text-[#1E1B4B] mb-3 flex items-center gap-2">
              <Clock size={14} className="text-violet-500" /> Latency (last {metrics.hours}h)
            </h3>
            <div className="space-y-3">
              {["gemini", "openai", "cached"].map((tier) => {
                const l = lat[tier] || { avg_ms: 0, p95_ms: 0, samples: 0 };
                const color = tier === "cached" ? "emerald" : tier === "gemini" ? "violet" : "blue";
                const icon = tier === "gemini" ? "✨" : tier === "openai" ? "🤖" : "📦";
                return (
                  <div key={tier} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span>{icon}</span>
                      <span className="text-xs text-[#1E1B4B] capitalize font-semibold">{tier}</span>
                      <span className="text-[10px] text-[#94A3B8]">({l.samples} jobs)</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs tabular-nums">
                      <span className={`text-${color}-700`}>avg <b>{(l.avg_ms / 1000).toFixed(1)}s</b></span>
                      <span className="text-[#64748B]">p95 {(l.p95_ms / 1000).toFixed(1)}s</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* CACHE COVERAGE + AI-GALLERY IMPORT */}
      <div className="bg-gradient-to-br from-violet-50 to-pink-50 border border-violet-200 rounded-2xl p-5" data-testid="magic-scale-import-panel">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
          <div>
            <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2">
              <Database size={14} className="text-violet-600" /> Cache pool ({cacheCount} images)
            </h3>
            <p className="text-xs text-[#64748B] mt-1">
              Richer pool = each customer sees a unique preview under surge load.
              Target: 10-20 images per (package · occasion) combo.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => runImport(true)}
              disabled={importing}
              className="px-3 py-2 bg-white border border-violet-300 text-violet-700 text-xs font-semibold rounded-xl hover:bg-violet-50 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="magic-scale-import-dryrun-btn"
            >
              {importing ? <RefreshCw size={12} className="animate-spin" /> : <ImageIcon size={12} />}
              Preview import
            </button>
            <button
              onClick={() => runImport(false)}
              disabled={importing}
              className="px-4 py-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-semibold rounded-xl disabled:opacity-50 flex items-center gap-1.5"
              data-testid="magic-scale-import-btn"
            >
              {importing ? <><RefreshCw size={12} className="animate-spin" /> Importing…</> : <><Upload size={12} /> Import from AI Gallery</>}
            </button>
          </div>
        </div>

        {importResult && (
          <div className="mt-3 px-3 py-2 bg-white border rounded-xl text-xs" data-testid="magic-scale-import-result">
            {importResult.error ? (
              <span className="text-rose-700">❌ {importResult.error}</span>
            ) : importResult.dry_run ? (
              <span>
                <b>Dry run:</b> would import <b>{importResult.would_import}</b> from {importResult.scanned} scanned ·
                dup: {importResult.skipped_dup} · no meta: {importResult.skipped_no_meta} ·
                invalid combo: {importResult.skipped_invalid_combo} · no storage: {importResult.skipped_no_storage}
              </span>
            ) : (
              <span className="text-emerald-700">
                ✅ Imported <b>{importResult.imported}</b> from AI Gallery ·
                cache is now <b>{importResult.cache_after?.count}</b> images
                across {importResult.cache_after?.bucket_count} combos.
              </span>
            )}
          </div>
        )}

        {thinCombos.length > 0 && (
          <div className="mt-4">
            <div className="text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-2">
              Combos needing more images ({thinCombos.length}):
            </div>
            <div className="flex flex-wrap gap-1.5">
              {thinCombos.map((c) => (
                <span key={`${c.package}-${c.occasion}`}
                      className={`text-[10px] px-2 py-1 rounded-md font-medium ${
                        c.count === 0 ? "bg-rose-100 text-rose-800 border border-rose-200"
                        : "bg-amber-100 text-amber-800 border border-amber-200"
                      }`}>
                  {c.package} · {c.occasion} ({c.count})
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
